import {
  Box,
  Collapse,
  Grid,
  Link,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Card from "../../common/Card";
import Table from "../../common/CustomTable";
import {
  AppRegistrationOutlined,
  ListAltOutlined,
  SearchOutlined,
  ReceiptLongOutlined,
  SegmentOutlined,
  GavelOutlined,
  Payment,
  UploadFile,
} from "@mui/icons-material";
import Button from "../../common/Button";
import { useLocation } from "react-router-dom";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import {
  SearchOrder,
  fetchOrderDocumentById,
  fetchOrdersList,
  resetOrderListInfo,
  useOrderSlice,
  fetchProductListIncludingInactiveByUserRoleOrderSlice,
  getPracticeListByUserRoleOrderSlicenew,
} from "../../../redux/slice/order/OrderSlice";
import { OrderInfo } from "../../../redux/slice/claim/ClaimSlice";
import { useEffect, useMemo, useState } from "react";
import { PracticeInfo } from "../../../redux/slice/practiceSlice";
import { AUDIT_ENTITYS, CAN_ACCESS_APPLICATION, ORDERTYPE, ORDER_ACTIONS, ORDER_INVOICE_STATUS, ORDER_STATUS, PRODUCT_STATUS, EXPORT_ACTIONS, PRODUCT_CATEGORY, CLAIM_STATUS, CLAIM_ACTIONS, USER_ROLE_ENTITY_NAMES } from "../../../constants/applicationConstants";
import { formatDate, hideElement, sortNames } from "../../../utils/Utility";
import {
  selectIsTokenAvailable,
  selectIsValueFoundInToken,
  selectTokenValues,
  useAuthSlice,
} from "../../../redux/slice/authSlice";
import { ProductInfo } from "../../../redux/slice/product/productListSlice";
import React from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import CustomIconButton from "../../common/CustomIconButton";
import PDFViewer from "../../common/pdf/PDFViewer";
import {
  fetchManufacturersList,
  setSingleAlertObj,
  showHideLoadingDialog,
  SingleAlertInfo,
  useCommonReducer,
} from "../../../redux/slice/commonSlice";
import {
  getSearchCriteriaFromSessionStorage,
  saveSelectionForPage,
  setSearchCriteriaToSessionStorage,
} from "../../../utils/StorageUtils";
import { tokens } from "../../../theme";
import CustomDateRange from "../../common/CustomDateRange";
import { DateObject } from "react-multi-date-picker";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import InfoPopup from "../../common/InfoPopup";
import { useNavigation } from "../../../hooks/useNavigation";
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import { getAllProviders, usePracticeProvidersSlice } from "../../../redux/slice/practice/practiceProvidersSlice";
import { useCreateNotificationSlice } from "../../../redux/slice/inbox/createNotificationSlice";
import CreateOrderIVRListPopup from "../orders/CreateOrderIVRListPopup";
import { fetchClaimList, useClaimSlice, updateClaimStatus, ClaimInfo, ClaimSearchRequest, } from "../../../redux/slice/claim/ClaimSlice";
import RecordAndEditPaymentPopup from "./RecordAndEditPaymentPopup";
import ClaimDocumentPopup from "./ClaimDocumentPopup";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

interface Row {
  orderNumber: string;
  practiceName: string;
  patientName: any;
  manufacturerName: string;
  productName: string;
  providerName: string;
  insurance?: string;
  orderDate: string;
  dateOfService: string;
  orderType: string;
  orderStatus: string | JSX.Element;
  aging?: number;
  action: JSX.Element | null;
}

type Option = {
  value: string;
  label: string;
};

const ITEMS_PER_PAGE = 20;

const ClaimList = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    control,
    reset,
    watch,
    setValue,
    getValues,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<SearchOrder>();

  const [showCard, setShowCard] = useState(false);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [showIVRListPopup, setShowIVRListPopup] = useState(false);
  const [showPDF, setShowPDF] = useState<{
    isShow: boolean;
    productName: string;
    fileName: string;
  }>({ isShow: false, productName: "", fileName: "" });

  const [isDateRangeChanged, setIsDateRangeChanged] = useState(false);
  const [isDateRangeServiceChanged, setIsDateRangeServiceChanged] =
    useState(false);
  const [isClearDateRange, setIsClearDateRange] = useState(false);

  const [searchCriteria, setSearchCriteria] = useState<ClaimSearchRequest>({
    providerId: "",
    patientLastName: "",
    patientFirstName: "",
    orderNumber: "",
    claimStatus: "",
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    fromServiceDate: "",
    toServiceDate: "",
  });

  const { userInfo } = useAuthSlice();
  const {
    orderListInfo,
    orderDocumentInfo,
    productsByUserRoleOrderSlice,
    practiceByUserRoleOrderSlice,
    practiceByUserRoleOrderSlicenew,
    cancelStatus,
  } = useOrderSlice();

  const {claimListInfo} = useClaimSlice();

  const { manufacturerInfoList } = useCommonReducer();

  const canCreateConsignmentOrder = useSelector((state: RootState) => selectIsTokenAvailable("canCreateConsignmentOrder")(state));
  const canCreateDirectOrder = useSelector((state: RootState) => selectIsTokenAvailable("canCreateDirectOrder")(state));
  const canRecordPastOrder = useSelector((state: RootState) => selectIsTokenAvailable("canRecordPastOrder")(state));
  const canAccessOrderActions = useSelector((state: RootState) => selectIsTokenAvailable("canAccessOrderActions")(state));
  const canReturnOrder = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessOrderActions", ORDER_ACTIONS.RETURN)(state));
  const canTrackOrder = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessOrderActions", ORDER_ACTIONS.TRACKING)(state));
  const canDetailOrder = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessOrderActions", ORDER_ACTIONS.DETAILS)(state));
  const canFulfilOrder = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessOrderActions", ORDER_ACTIONS.FULFILL)(state));
  const canBillOrder = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessOrderActions", ORDER_ACTIONS.BILLING)(state));
  const canViewHiddenOrderColumns = useSelector((state: RootState) => selectTokenValues("canViewHiddenOrderColumns")(state));
  const canReOrder = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessOrderActions", ORDER_ACTIONS.REORDER)(state));
  const canCancelOrder = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessOrderActions", ORDER_ACTIONS.CANCEL)(state));
  const canAuditOrder = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAuditAction", AUDIT_ENTITYS.USERAUDIT)(state));
  const canAccessClaimAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessClaimAction", CLAIM_ACTIONS.CLAIMSECTION)(state));
  const canAccessExportSSDetail = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessExportExcel", EXPORT_ACTIONS.EXPORT_SS_DETAIL)(state));
  const canAccessProviderProducts = useSelector((state: RootState) => selectTokenValues("canAccessProviderProducts")(state));
  const canExportList = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessOrderActions", ORDER_ACTIONS.EXPORT)(state));
  const accessibleNotifications = useSelector((state: RootState) => selectTokenValues("canAccessNotifications")(state));
  const [totalNotificationIds, setTotalNotificationIds] = useState([]);
  const [showOptionPopup, setShowOptionsPopup] = React.useState<OptionPopupObjectProps>({ message: '', show: false });
  const [ loadingComplete, setLoadingComplete ] = useState<Boolean>(false);
  const [fetchPracticeAndProductsCompleted, setFetchPracticeAndProductsCompleted] = useState<Boolean>(false);
  const [fetchManufacturerListCompleted, setFetchManufacturerListCompleted] = useState<Boolean>(false);
  const [fetchProvidersCompleted, setFetchProvidersCompleted] = useState<boolean>(false);
    

  const [activeTab, setActiveTab] = useState("NOT_READY");

  const [filterApplied, setFilterApplied] = useState(false);
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
  //CS-1071 - Start
  const [activeButtonId, setActiveButtonId] = useState<string | null>(null);
  //CS-1071 - End
  const [filteredeProductsByUserRoleOrderSlice, setFilteredeProductsByUserRoleOrderSlice] = useState<ProductInfo[]>();
  const [showCreateOrderPopup, setCreateOrderPopup] = useState<{message:string, show: boolean, title: string}>({message: "", show: false, title: "" });
  const { getProviderList } = usePracticeProvidersSlice();
  const watchedPracticeIds = watch("practiceIds");
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null);
  const [statusInfo, setStatusInfo] = useState<{ orderNumber: string; orderStatus: string }>({
    orderNumber: "",
    orderStatus: "",
  });
  const [isAlertOnly, setIsAlertOnly] = useState<boolean>(false);
  const [ openRecordPayment, setOpenRecordPayment ] = useState<boolean>(false);
  const [ selectedClaim, setSelectedClaim] = useState<any>();
  const [navigateToDetails, setNavigateToDetails] = useState<Boolean>(false);
  const [ viewDocumentPopup, setViewDocumentPopup ] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const saved = sessionStorage.getItem("activeActionButtonId");
    if (saved) {
      setActiveButtonId(saved);
    }
  }, []);

  useEffect(() => {
    const accessibleProductIds = canAccessProviderProducts[0] ? canAccessProviderProducts[0].split(',') : [];
    if(canAccessProviderProducts){
      if (accessibleProductIds && accessibleProductIds.length > 0) {
        const filteredProducts = productsByUserRoleOrderSlice.filter((product) =>
          accessibleProductIds.includes(product.productId.toString())
        );
        setFilteredeProductsByUserRoleOrderSlice(filteredProducts);
      } else {
        setFilteredeProductsByUserRoleOrderSlice([])
      }
    }else{
      setFilteredeProductsByUserRoleOrderSlice(productsByUserRoleOrderSlice);
    }
  }, [productsByUserRoleOrderSlice, canAccessProviderProducts]);


  const hiddenColumns = useMemo(() => {
    let cols: Column[] = [
      { id: "invoiceStatus", label: "Invoice Status", minWidth: 100 },
    ];
    return cols;
  }, []);

  const columns = useMemo(() => {
    let cols: Column[] = [
      { id: "orderNumber", label: "Order ID", minWidth: 100 },
      { id: "patientName", label: "Patient", minWidth: 100 },
      { id: "insurance", label: "Insurance", minWidth: 100 },
      { id: "providerName", label: "Provider", minWidth: 100 },
      { id: "orderDate", label: "Order Date", minWidth: 100 },
    ];

    if (activeTab === "NOT_READY") {
      cols.push({ id: "aging", label: " Claim Aging", minWidth: 100 });
    }

    canViewHiddenOrderColumns &&
      canViewHiddenOrderColumns.forEach((colId) => {
        const hideCol = hiddenColumns.find((col) => col.id == colId);
        if (hideCol) {
          cols.push(hideCol);
        }
      });
    cols.push({ id: "action", label: "Action", minWidth: 50 });
    return cols;
  }, [canViewHiddenOrderColumns, activeTab]);

  const orderStatusOptions = useMemo(() => {
    let options: { label: string; value: any }[] = [
      { value: PRODUCT_STATUS.PLACED, label: PRODUCT_STATUS.PLACED },
      { value: PRODUCT_STATUS.SHIPPED, label: PRODUCT_STATUS.SHIPPED },
      { value: PRODUCT_STATUS.DELIVERED, label: PRODUCT_STATUS.DELIVERED },
      { value: PRODUCT_STATUS.CANCELED, label: PRODUCT_STATUS.CANCELED },
      { value: PRODUCT_STATUS.PROCESSING, label: PRODUCT_STATUS.PROCESSING },
      { value: PRODUCT_STATUS.DELAYED, label: PRODUCT_STATUS.DELAYED },
    ];
    return options;
  }, []);

  const statusOptions = useMemo(() => {
    return Object.entries(CLAIM_STATUS).map(([key, label]) => ({
      value: key,
      label
    }));
  }, []);

  const orderInvoiceStatusOptions = useMemo(() => {
    let options: { label: string; value: any }[] = [
      {
        value: ORDER_INVOICE_STATUS.PENDING,
        label: ORDER_INVOICE_STATUS.PENDING,
      },
      {
        value: ORDER_INVOICE_STATUS.PARTIAL,
        label: ORDER_INVOICE_STATUS.PARTIAL,
      },
      {
        value: ORDER_INVOICE_STATUS.INVOICED,
        label: ORDER_INVOICE_STATUS.INVOICED,
      },
    ];
    return options;
  }, []);

  const practiceOptions = useMemo(() => {
    let options: Option[] = [];
    if (
      practiceByUserRoleOrderSlicenew &&
      practiceByUserRoleOrderSlicenew.length > 0
    ) {
      if (practiceByUserRoleOrderSlicenew.length === 1) {
        setValue("practiceIds", [
          Number(practiceByUserRoleOrderSlicenew[0]?.practiceId),
        ]);
      }
      const sortedPracticeInfo: PracticeInfo[] = sortNames(
        practiceByUserRoleOrderSlicenew,
        "practiceName"
      );
      sortedPracticeInfo?.map((practice) => {
        options.push({
          value: practice.practiceId,
          label: practice.practiceName,
        });
      });
    }
    return options;
  }, [practiceByUserRoleOrderSlicenew]);

  //Mount
  useEffect(() => {
    fetchInitialLists();
    const orderDocId = location.state?.orderDocId;
    const tissueTrackingDocId = location.state?.tissueTrackingDocId;
    const productReturnDocId = location.state?.productReturnDocId;
    const orderNumber = location.state?.orderNumber;
    if (orderNumber) {
      setValue("orderNumber", orderNumber);
    }
    if (orderDocId || tissueTrackingDocId || productReturnDocId) {
      previewDoc(orderDocId || tissueTrackingDocId || productReturnDocId);
    }
  }, []);

  useEffect(() => {
    const fechManufacturers = async () =>{
      const res = await dispatch(fetchManufacturersList());
      if(res){
        setFetchManufacturerListCompleted(true)
      }
    }
    fechManufacturers();
  }, []);

  const setValuesToCriteriaFields = (searchCriteria: ClaimSearchRequest) => {
    setValue("patientFirstName", searchCriteria.patientFirstName);
    setValue("patientLastName", searchCriteria.patientLastName);
    setValue("orderNumber", searchCriteria.orderNumber);
    setValue("providerName", searchCriteria.providerId);

    setValue("fromDate", searchCriteria.fromServiceDate);
    setValue("toDate", searchCriteria.toServiceDate);

    setValue("fromServiceDate", searchCriteria.fromServiceDate);
    setValue("toServiceDate", searchCriteria.toServiceDate);
  }

  const previewDoc = async (docId: string) => {
    const actionResult = await dispatch(
      fetchOrderDocumentById(parseInt(docId))
    );
    const documentData = actionResult.payload;

    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: docId + ` - Order`,
        fileName: docId,
      });
    }
  };

  const fetchInitialLists = async () => {
    dispatch(showHideLoadingDialog(true));
    //Don't remove below unused variables
    const responsePractices = await dispatch(
      // getPracticeListByUserRoleOrderSlice(false)
      getPracticeListByUserRoleOrderSlicenew({isActivePracticeOnly: true, skipLoader: false})
    );
    const responseProducts = await dispatch(
      fetchProductListIncludingInactiveByUserRoleOrderSlice({includeProductPrice: false, executedOnly: true, skipLoader: false})
    );
    if(responsePractices && responseProducts){
      setFetchPracticeAndProductsCompleted(true)
    }
    dispatch(showHideLoadingDialog(false));
  };

  //Unmount
  useEffect(
    () => () => {
      dispatch(resetOrderListInfo());
    },
    []
  );

  const onSubmit = (data: any) => {
    if (!isDateRangeChanged) {
      setIsClearDateRange(true);
    }
    const data2: ClaimSearchRequest = {
      claimStatus: activeTab,
      orderNumber: data.orderNumber,
      patientFirstName: data.patientFirstName,
      patientLastName: data.patientLastName,
      providerId: data.providerName,
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      fromServiceDate: isDateRangeServiceChanged ? data.fromDate : "",
      toServiceDate: isDateRangeServiceChanged ? data.toDate : "",
    };
    setSearchCriteria(data2);
    setHasSearched(true);

    setSearchCriteriaToSessionStorage(CAN_ACCESS_APPLICATION.CLAIM_MANAGEMENT + "SearchCriteria" + userInfo.userId, data2);

    // if (data2.patientFirstName === "" && data2.patientLastName === "" && data2.providerId === "" && (data2.orderNumber === undefined || data2.orderNumber === "")) {
    //   setFilterApplied(false);
    // } else {
    //   setFilterApplied(true);
    // }
  };

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      claimStatus: activeTab,
      currentPage: pageNumber + 1,
    };
    dispatch(fetchClaimList(data))
  };

  const handleClose = () => {
    setShowIVRListPopup(false);
  };

  const handleClaimSection = (row: OrderInfo) => {
    navigate("/record_claim_section", { state: { selectedOrderBilling: row } });
  };

  const handleRecordClaim = (row: OrderInfo) => {
    navigate("/record_claim", { state: { selectedOrderBilling: row } });
  };

  const handleActionDetails = async (row: OrderInfo) => {
    const practiceIdList =
      practiceByUserRoleOrderSlicenew && practiceByUserRoleOrderSlicenew.length > 0
        ? practiceByUserRoleOrderSlicenew?.map((practice) =>
          Number(practice.practiceId)
        )
        : [];
    const productIdList =
      filteredeProductsByUserRoleOrderSlice && filteredeProductsByUserRoleOrderSlice.length > 0
        ? filteredeProductsByUserRoleOrderSlice?.map((product) =>
          Number(product.productId)
        )
        : [];
    const manufacturerIdsList =
      manufacturerInfoList && manufacturerInfoList?.length > 0
        ? manufacturerInfoList?.map((manufacturer) =>
          Number(manufacturer.manufacturerId)
        ) : [];


    const searchCriteria: SearchOrder = {
      practiceIds:
        practiceIdList && practiceIdList.length > 0 ? practiceIdList : [],
      manufacturerIds:
        manufacturerIdsList && manufacturerIdsList.length > 0 ? manufacturerIdsList : [],
      productIds:
        productIdList && productIdList.length > 0 ? productIdList : [],
      patientFirstName: "",
      patientLastName: "",
      providerName: "",
      orderNumber: row.orderNumber,
      invoiceNumber: "",
      serialNumber: "",
      fromDate: "",
      toDate: "",
      status: orderStatusOptions && orderStatusOptions.length > 0
        ? orderStatusOptions?.map((status) => status.value)
        : [],
      invoiceStatus: orderInvoiceStatusOptions && orderInvoiceStatusOptions.length > 0
        ? orderInvoiceStatusOptions?.map((status) => status.value)
        : [],
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      downloadState: false,
      fromServiceDate: "",
      toServiceDate: "",
      withoutIvr: "",
      alertOrders: false,
    };

    await dispatch(fetchOrdersList({data: searchCriteria, skipLoader:false})).unwrap();

    setNavigateToDetails(true)

  };

  // useEffect(() => {
  //   let criteria = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.CLAIM_MANAGEMENT + "SearchCriteria" + userInfo.userId);
  //   if (criteria) {
  //     setValuesToCriteriaFields(criteria)
  //     dispatch(fetchClaimList(criteria)).unwrap();
  //     setActiveTab(criteria.claimStatus)
  //   }
  //   if (hasSearched) {
  //     dispatch(fetchClaimList(searchCriteria)).unwrap();
  //   }
  // }, [searchCriteria, hasSearched])

  useEffect(() => {
    if (orderListInfo?.content[0]?.orderId && navigateToDetails) {
      navigate("/details/1", { state: { orderDetails: orderListInfo?.content[0] } });
      setNavigateToDetails(false)
    }

  }, [orderListInfo, navigateToDetails])


  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  }

  const handleMarkAsReady = async (selectedClaim: OrderInfo) => {

    {
      setShowOptionsPopup({
        message: "Do you want mark this claim as ready?",
        show: true,
        variant: "confirm",
        buttons: [{ name: "Confirm", color: 'primary', onClick: () => markClaimAsReady(selectedClaim) }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
      })
    }

  }

  const markClaimAsReady = async (claimData: OrderInfo) => {
    if (claimData.claimId) {
      let criteria = {
        ...searchCriteria,
        claimStatus: activeTab
      }
      const response = await dispatch(updateClaimStatus({
        claimId: Number(claimData.claimId),
        claimStatus: CLAIM_STATUS.READY
      }
      ));
      if (response.payload && response.payload.data.message === 'SUCCESS') {
        const alertObj: SingleAlertInfo = {
          message: "Claim has been marked as ready",
          alertType: "success",
        };
        cancelOptionPopup();
        dispatch(fetchClaimList(criteria))
        dispatch(setSingleAlertObj(alertObj));
      } else {
        const alertObj: SingleAlertInfo = {
          message: "Error marking the claim as ready",
          alertType: "error",
        };
        cancelOptionPopup();
        dispatch(fetchClaimList(criteria))
        dispatch(setSingleAlertObj(alertObj));
      }

    }
  }


  const handleMarkClaimed = (selectedClaim: OrderInfo) => {

    {
      setShowOptionsPopup({
        message: "Do you want to mark this as claimed?",
        show: true,
        variant: "confirm",
        buttons: [{ name: "Confirm", color: 'primary', onClick: () => markAsClaimedById(selectedClaim) }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
      })
    }
  }

  const markAsClaimedById = async (claimData: OrderInfo) => {
    if (claimData.claimId) {
      let criteria = {
        ...searchCriteria,
        claimStatus: activeTab
      }
      const response = await dispatch(updateClaimStatus({
        claimId: Number(claimData.claimId),
        claimStatus: "CLAIM_FILED"
      }
      ));
      if (response.payload && response.payload.data.message === 'SUCCESS') {
        const alertObj: SingleAlertInfo = {
          message: "Marked as Claimed",
          alertType: "success",
        };
        cancelOptionPopup();
        dispatch(fetchClaimList(criteria))
        dispatch(setSingleAlertObj(alertObj));
      } else {
        const alertObj: SingleAlertInfo = {
          message: "Error marking as claimed",
          alertType: "error",
        };
        cancelOptionPopup();
        dispatch(fetchClaimList(criteria))
        dispatch(setSingleAlertObj(alertObj));
      }

    }
  }

  const handleRecordPayment = (selectedClaim1: OrderInfo) => {
    console.log("selectedclaim ", selectedClaim1)
    setSelectedClaim(selectedClaim1)
    setOpenRecordPayment(true);
  }

  const uploadEOBDocument = (selectedClaim: OrderInfo) => {
    setSelectedClaim(selectedClaim)
    setViewDocumentPopup(true)
  }

  const handleMarkUnresolved = (selectedClaim: OrderInfo) => {

    {
      setShowOptionsPopup({
        message: "Do you want to mark this as unresolved?",
        show: true,
        variant: "confirm",
        buttons: [{ name: "Confirm", color: 'primary', onClick: () => markUnresolved(selectedClaim) }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
      })
    }
  }

  const markUnresolved = async (claimData: OrderInfo) => {
    if (claimData.claimId) {
      let criteria = {
        ...searchCriteria,
        claimStatus: activeTab
      }
      const response = await dispatch(updateClaimStatus({
        claimId: Number(claimData.claimId),
        claimStatus: CLAIM_STATUS.UNRESOLVED
      }
      ));
      if (response.payload && response.payload.data.message === 'SUCCESS') {
        const alertObj: SingleAlertInfo = {
          message: "Marked as Unresolved",
          alertType: "success",
        };
        cancelOptionPopup();
        dispatch(fetchClaimList(criteria))
        dispatch(setSingleAlertObj(alertObj));
      } else {
        const alertObj: SingleAlertInfo = {
          message: "Error marking as unresolved",
          alertType: "error",
        };
        cancelOptionPopup();
        dispatch(fetchClaimList(criteria))
        dispatch(setSingleAlertObj(alertObj));
      }

    }
  }

    const handleMarkComplete = (selectedClaim: OrderInfo) => {

    {
      setShowOptionsPopup({
        message: "Do you want to mark this as completed?",
        show: true,
        variant: "confirm",
        buttons: [{ name: "Confirm", color: 'primary', onClick: () => markAsComplete(selectedClaim) }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
      })
    }
  }

    const markAsComplete = async (claimData: OrderInfo) => {
    if (claimData.claimId) {
      let criteria = {
        ...searchCriteria,
        claimStatus: activeTab
      }
      const response = await dispatch(updateClaimStatus({
        claimId: Number(claimData.claimId),
        claimStatus: CLAIM_STATUS.COMPLETED
      }
      ));
      if (response.payload && response.payload.data.message === 'SUCCESS') {
        const alertObj: SingleAlertInfo = {
          message: "Marked as Completed",
          alertType: "success",
        };
        cancelOptionPopup();
        dispatch(fetchClaimList(criteria))
        dispatch(setSingleAlertObj(alertObj));
      } else {
        const alertObj: SingleAlertInfo = {
          message: "Error marking as completed",
          alertType: "error",
        };
        cancelOptionPopup();
        dispatch(fetchClaimList(criteria))
        dispatch(setSingleAlertObj(alertObj));
      }

    }
  }

    const handleActionButtonClick = (buttonId: string, originalClick: () => void) => {
      const currentPageKey = "orderList"; // Or dynamically set this value based on the current page
      saveSelectionForPage(currentPageKey, buttonId);
      setActiveButtonId(buttonId);
      sessionStorage.setItem("activeActionButtonId", buttonId);
      originalClick();
  };

    const handleViewIVR = (selectedClaim: OrderInfo) =>{
       const ivrInfo = {
              ivrId: selectedClaim.ivrId,
              ivrNumber: selectedClaim.ivrNumber,
              dateCreated: selectedClaim.ivrLastUpdateTime,
              patient: selectedClaim.patientName,
              patientDOB: selectedClaim.patientDOB
        };
        navigate("/ivr_details", { state: { ivrInfo: ivrInfo } });
    }

  const actionComponent = (row: OrderInfo): JSX.Element | null => {
    const conditions = [
      hideElement(!canDetailOrder),
      hideElement(!(canFulfilOrder && row?.orderStatus !== ORDER_STATUS.CANCELED)),
      hideElement(
        row.productCategory === PRODUCT_CATEGORY.CONSERVATIVE ||
        !canBillOrder ||
        row?.orderStatus !== ORDER_STATUS.DELIVERED ||
        row.invoiceStatus === "INVOICED"
      ),
      hideElement(
        row?.productCategory === PRODUCT_CATEGORY.CONSERVATIVE ||
        !canReOrder ||
        row?.orderStatus !== ORDER_STATUS.PLACED
      ),
      hideElement(
        row.productCategory === PRODUCT_CATEGORY.CONSERVATIVE ||
        !(canTrackOrder && row?.orderStatus === ORDER_STATUS.DELIVERED)
      ),
      hideElement(
        row.productCategory === PRODUCT_CATEGORY.CONSERVATIVE ||
        !(canReturnOrder && row?.orderStatus === ORDER_STATUS.DELIVERED)
      ),
      hideElement(
        !canCancelOrder || !(
          row?.orderStatus === ORDER_STATUS.PLACED ||
          ((userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && row?.orderStatus === ORDER_STATUS.PROCESSING && row?.trackingNumber === null) ||
            (userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && row?.orderStatus === ORDER_STATUS.PROCESSING))
        )
      ),
      hideElement(!canAuditOrder),
    ];

    if (conditions.every(display => display === "none")) return null;

    return (
      <>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          onClick={() =>handleActionButtonClick(`orderListActionButton-${row.orderNumber}`, () =>  handleActionDetails(row))}
          tooltipMessage="Detail"
          tooltipPlacement="top"
          style={{display: `${hideElement(!canDetailOrder)}`}}
        >
          <ListAltOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="success"
          size="small"
          variant="outlined"
          onClick={() =>  handleActionButtonClick(`orderListActionButton-${row.orderNumber}`, () => handleRecordClaim(row))}
          tooltipMessage="Record Claim"
          tooltipPlacement="top"
          style={{ display: `${hideElement(activeTab !== CLAIM_STATUS.NOT_READY)}` }}
        >
          <ReceiptLongOutlined fontSize="small" />
        </CustomIconButton>
        {row.ivrId &&
          <CustomIconButton
            color="success"
            size="small"
            variant="outlined"
            onClick={() =>  handleViewIVR(row)}
            tooltipMessage="View IVR"
            tooltipPlacement="top"
          >
            <ReceiptLongOutlined fontSize="small" />
          </CustomIconButton>
        }
        <CustomIconButton
          color="success"
          size="small"
          variant="outlined"
          onClick={() =>  handleMarkAsReady(row)}
          tooltipMessage="Mark as Ready"
          tooltipPlacement="top"
          style={{ display: `${hideElement(activeTab !== "NOT_READY")}` }}
        >
          <ReceiptLongOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="success"
          size="small"
          variant="outlined"
          onClick={() =>  handleMarkClaimed(row)}
          tooltipMessage="Mark Claimed"
          tooltipPlacement="top"
          style={{ display: `${hideElement(activeTab !== CLAIM_STATUS.READY)}` }}
        >
          <GavelOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="success"
          size="small"
          variant="outlined"
          onClick={() =>  handleRecordPayment(row)}
          tooltipMessage="Record Payment"
          tooltipPlacement="top"
          style={{ display: `${hideElement(activeTab !== "CLAIM_FILED" && activeTab !== CLAIM_STATUS.COMPLETED)}` }}
        >
          <Payment fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          onClick={() => uploadEOBDocument(row)}
          tooltipMessage="Upload EOB"
          tooltipPlacement="top"
          style={{ display: `${hideElement(activeTab === CLAIM_STATUS.READY || activeTab === "NOT_READY")}` }}
        >
          <UploadFile fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          onClick={() => handleMarkUnresolved(row)}
          tooltipMessage="Mark Unresolved"
          tooltipPlacement="top"
          style={{ display: `${hideElement(activeTab !== "CLAIM_FILED")}` }}
        >
          <GavelOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          onClick={() => handleMarkComplete(row)}
          tooltipMessage="Mark as Complete"
          tooltipPlacement="top"
          style={{ display: `${hideElement(activeTab !== CLAIM_STATUS.UNRESOLVED)}` }}
        >
          <GavelOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Record Claim Section"
          color="info"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canAccessClaimAction)}` }}
          onClick={() =>  handleActionButtonClick(`orderListActionButton-${row.orderNumber}`, () => handleClaimSection(row))}
        >
          <SegmentOutlined fontSize="small" />
        </CustomIconButton>
      </>
    );
  };

  const handleDateRangeChange = (
    fromDate: DateObject | null,
    toDate: DateObject | null
  ) => {
    const fromDateString = fromDate ? fromDate.format("MM/DD/YYYY") : null;
    const toDateString = toDate ? toDate.format("MM/DD/YYYY") : null;
    setValue("fromDate", fromDateString ? fromDateString : "");
    setValue("toDate", toDateString ? toDateString : "");
    setIsDateRangeServiceChanged(true);

    setSearchCriteria((prevCriteria) => ({
      ...prevCriteria,
      fromServiceDate: fromDateString || "",
      toServiceDate: toDateString || "",
    }));

    setIsDateRangeChanged(true);
  };

  function calculateClaimAging(claimDueDate: any) {

    const dueDate = new Date(claimDueDate);
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();

    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }


  const handlePatientDetailsNavigate = (details: any) => {
    navigate('/patientDetails', {
      state: {
        patientDetails: { patientId: details?.patientId, patientName: details?.patientName },
      }
    })
  }

  const claimListData: Row[] = useMemo(() => {
    let listData: Row[] = [];

    if (claimListInfo) {
      let list = claimListInfo?.content || [];
      if (activeTab === CLAIM_STATUS.NOT_READY) {
        list = list.filter(
          (claim) =>
            claim.claimStatus === null
        );
      }

      return list.map((details: any) => ({
        orderNumber: details.orderNumber,
        claimNumber: details.claimNumber,
        claimId: details.claimId,
        cmsDocId: details.cmsDocumentId,
        practiceName: details.practiceName,
        patientName:
          (
            <Link
              component="button"
              underline="hover"
              variant="body2"
              onClick={() => handlePatientDetailsNavigate(details)}
              color={colors.info[300]}
            >
              {details.patientName}
            </Link>
          ),
        insurance: details.insuranceName,
        manufacturerName: details.manufacturerName,
        manufacturerId: details.manufacturerId,
        productName: details.productName,
        providerName: details.providerName || details.orderInfo?.provider,
        orderDate: formatDate(details.orderDate),
        dateOfService: formatDate(details.dateOfService),
        aging: calculateClaimAging(details.claimDueDate),
        orderType: details.orderType,
        orderStatus: (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="body2"
              sx={{ display: "inline-block" }}
            >
              {
                details.orderItems?.find((item: any) => item.claimId === details.claimId)?.claimStatus
                || ''
              }
            </Typography>
          </Box>
        ),

        productCategory: details.productCategory,
        action: actionComponent(details),
      }));
    }
    return listData;
  }, [claimListInfo]);

  useEffect(() => {
    const fetchProviders = async () => {
      const res = await dispatch(getAllProviders());
      if (res) {
        setFetchProvidersCompleted(true)
      }
    }
    fetchProviders();
  }, []);

  const practiceProviderList = useMemo(() => {
    let options: { label: string; value: any }[] = [];

    if (Array.isArray(getProviderList)) {
      const filteredProviders = watchedPracticeIds?.length > 0
        ? getProviderList.filter(provider =>
          watchedPracticeIds.includes(Number(provider.practiceId))
        )
        : getProviderList;

      filteredProviders.forEach((item) => {
        options.push({
          label: `${item.title ? item.title + " " : ""}${item.firstName} ${item.middleName ? item.middleName + " " : ""}${item.lastName}`,
          value: item.providerId,
        });
      });
    }

    return options;
  }, [getProviderList, watchedPracticeIds]);

  const tabs = useMemo(() => [...statusOptions], [statusOptions]);

  useEffect(() => {
    let criteria = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.CLAIM_MANAGEMENT + "SearchCriteria" + userInfo.userId);
    if (criteria) {
      setValuesToCriteriaFields(criteria);
      setActiveTab(criteria.claimStatus)
    }
  }, []);

  useEffect(() => {
    let criteria = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.CLAIM_MANAGEMENT + "SearchCriteria" + userInfo.userId);
    if (criteria) {
      dispatch(fetchClaimList({ ...criteria, claimStatus: activeTab })).unwrap();
    } else {
      const data = {
        ...searchCriteria,
        providerId: searchCriteria.providerId ? searchCriteria.providerId : "",
        claimStatus: activeTab
      }
      dispatch(fetchClaimList(data))
    }
  }, [activeTab, searchCriteria]);

  useEffect(() => {
    if (fetchManufacturerListCompleted &&
      fetchPracticeAndProductsCompleted &&
      fetchProvidersCompleted
    ) {
      setLoadingComplete(true)
    }
  }, [fetchManufacturerListCompleted, fetchProvidersCompleted, fetchPracticeAndProductsCompleted
  ])

  return (
    <Box my="8px" ml="12px" mr="10px">
      {/*   CS-793  */}
      {showCreateOrderPopup.show &&
        <InfoPopup
          open={showCreateOrderPopup.show}
          content={showCreateOrderPopup.message}
          onClose={() => setCreateOrderPopup({ message: "", show: false, title: "" })}
          severity="info"
          confirmButton={"OK"}
          onConfirm={() => {navigate('/patients')}}
          title={showCreateOrderPopup.title}
        />
      }
      {showIVRListPopup && (
        <CreateOrderIVRListPopup
          open={showIVRListPopup}
          onClose={handleClose}
        />
      )}
      {showPDF.isShow && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() =>
            setShowPDF({ isShow: false, productName: "", fileName: "" })
          }
          title={showPDF.productName}
          fileName={showPDF.fileName}
          base64String={orderDocumentInfo?.content || ""}
        />
      )}
      {showOptionPopup.show &&
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      }
      {openRecordPayment && (
        <RecordAndEditPaymentPopup
          open={openRecordPayment}
          onClose={() => setOpenRecordPayment(false)}
          onSuccess={() => dispatch(fetchClaimList({ ...searchCriteria, claimStatus: activeTab }))}
          claimId={selectedClaim.claimId}
        />
      )}
      {viewDocumentPopup && (
        <ClaimDocumentPopup
          open={viewDocumentPopup}
          onClose={() => { setViewDocumentPopup(false) }}
          claimId={selectedClaim.claimId}
          onUploadSuccess={() => {
            fetchClaimList(
              {
                ...searchCriteria,
                claimStatus: activeTab
              }
            )
          }}
        />
      )}
      <Box mb="15px">
        <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
          <Card>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={1.5} mt={-3} mb={-1}>
                {/* <Grid item md={3} xs={12}>
                  <CustomMultipleDropDown
                    name="practiceIds"
                    label="Practice"
                    disabled={practiceByUserRoleOrderSlicenew?.length === 1}
                    options={practiceOptions}
                    control={control}
                  />
                </Grid> */}
                {/* CS-905 */}
                <Grid item  md={3} xs={12}>
                  <CustomTextField
                    control={control}
                    controllerName="patientFirstName"
                    label="Patient First Name"
                  />
                </Grid>
                <Grid item md={3} xs={12}>
                  <CustomTextField
                    control={control}
                    controllerName="patientLastName"
                    label="Patient Last Name"
                  />
                </Grid>

                <Grid item md={3} xs={12}>
                  <CustomTextField
                    control={control}
                    controllerName="orderNumber"
                    label="Order ID"
                  />
                </Grid>

                <Grid item md={3} xs={12}>
                  <CustomDropdownField
                    control={control}
                    name="providerName"
                    label="Provider"
                    options={practiceProviderList}
                  />
                </Grid>

                <Grid item md={3} xs={12}>
                  {filterApplied}
                  <CustomDateRange
                    label="Order Date Range"
                    control={control}
                    controllerName="dateRange"
                    onChange={handleDateRangeChange}
                    dateRangeMonths={filterApplied ? 0 : 6}
                    onClear={isClearDateRange}
                  />
                  {/* // */}
                </Grid>
                <Grid item md={2} xs={12}>
                  <Button
                    fullWidth
                    size="large"
                    startIcon={<SearchOutlined />}
                    style={{ padding: "15px" }}
                    type="submit"
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Card>
        </Collapse>
      </Box>
      <Box>
        <Card>
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            spacing={1}
            mt={-2}
          >
            <Grid item xs={12} md>
              <Typography variant='h3' fontWeight="bold">Claim List</Typography>
            </Grid>
            <Grid item xs={12} md="auto">
              <Button
                variant="outlined"
                color="info"
                onClick={toggleFilterPanel}
                startIcon={<SearchOutlined />}
                fullWidth
              >
                {showCard ? "Hide Filters" : "Show Filters"}
              </Button>
            </Grid>
            {canCreateConsignmentOrder &&
              <Grid item xs={12} md="auto">
                <Button
                  fullWidth
                  color="warning"
                  startIcon={<AppRegistrationOutlined />}
                  onClick={() =>
                    navigate("/order", {
                      state: { orderType: ORDERTYPE.CONSIGNMENT_ORDER },
                    })
                  }
                >
                  Create Consignment Order
                </Button>
              </Grid>
            }
          </Grid>

          <Box sx={{backgroundColor : colors.grey[900]}}>
            <Tabs
              value={tabs.findIndex((tab) => tab.value === activeTab)}
              onChange={(e, newVal) => setActiveTab(tabs[newVal].value)}
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="secondary"
              textColor="secondary"
            >
              {tabs.map((status) => (
                <Tab key={status.value} label={status.label} />
              ))}
            </Tabs>
          </Box>


          <Table
            columns={columns}
            // rows={orderListInfo?.content || []}
            rows={loadingComplete ? claimListData : []}
            actionComponent={(row: OrderInfo) => actionComponent(row)}
            emptyText="No Claims Found"
            totalPages={loadingComplete ? claimListInfo?.totalPages : 0}
            totalElementText={
              "Records: " +
              ((loadingComplete && claimListInfo?.totalElements)
                ? claimListInfo?.totalElements
                : "0")
            }
            pageNumber={handlePagination}
            //@ts-ignore
            currentPageNumber={searchCriteria.currentPage - 1}
            showMenu={true}
            sortableColumns={["orderNumber", "practiceName", "patientName", "orderDate", "dateOfService"]}
            // CS-1071 - Start
            currentPageKey={"orderList"}
            uniqueValue="orderNumber"
            activeButtonId={activeButtonId}
          // CS-1071 - End
          />
        </Card>
      </Box>
    </Box>
  );
};

export default ClaimList;
